import { defineStore } from 'pinia'
import { ref, watch, type WatchStopHandle } from 'vue'
import { getRoutingApi } from '../api/routing'
import type { TeamLoad } from '../api/routing/types'
import { useInbox } from './inbox'

/**
 * Live team load for the strip under the chat list (TeamLoad.vue): GET /api/v1/team/load
 * - right away, every 20 s (people go idle / shifts start with time), and
 * - about a second after any chat changes owner, status or "needs reply", or anyone's พร้อมรับ changes (realtime
 *   events update the inbox store; we watch a tiny signature of it), so the bars follow without polling hard.
 * Each result also refreshes the inbox store's own copies (me/team open_count + cap, queue, assign mode), so
 * "ดูแลอยู่ 4 จาก 10 แชท" in the list header always matches the strip.
 */
export const useRouting = defineStore('routing', () => {
  const load = ref<TeamLoad | null>(null)
  const error = ref<string | null>(null)
  let users = 0
  let timer: ReturnType<typeof setInterval> | null = null
  let soon: ReturnType<typeof setTimeout> | null = null
  let stopWatch: WatchStopHandle | null = null
  let seq = 0

  async function refresh(): Promise<void> {
    const mine = ++seq
    try {
      const got = await getRoutingApi().teamLoad()
      if (mine !== seq) return
      load.value = got
      error.value = null
      sync(got)
    } catch (e) {
      if (mine === seq) error.value = e instanceof Error ? e.message : String(e)
    }
  }

  function sync(got: TeamLoad): void {
    const inbox = useInbox()
    const by = new Map(got.members.map((m) => [m.id, m]))
    for (const t of inbox.team) {
      const m = by.get(t.id)
      if (m) {
        t.open_count = m.load
        t.cap = m.cap
      }
    }
    const me = inbox.me ? by.get(inbox.me.id) : undefined
    if (inbox.me && me) {
      inbox.me.open_count = me.load
      inbox.me.cap = me.cap
    }
    inbox.queueWaiting = got.queue.waiting
    if (got.method !== inbox.settings.assign_mode) inbox.settings = { ...inbox.settings, assign_mode: got.method }
  }

  /** Refresh after the burst settles. */
  function schedule(ms = 900): void {
    if (soon) clearTimeout(soon)
    soon = setTimeout(() => {
      soon = null
      void refresh()
    }, ms)
  }

  /** TeamLoad.vue mounts → start; unmounts → stop (reference counted). */
  function start(): void {
    users += 1
    if (users > 1) return
    void refresh()
    timer = setInterval(() => void refresh(), 20000)
    const inbox = useInbox()
    stopWatch = watch(
      () =>
        Object.values(inbox.convs)
          .map((c) => `${c.id}:${c.assigned_staff?.id ?? ''}:${c.needs_reply ? 1 : 0}:${c.status}`)
          .join('|') +
        // someone's พร้อมรับ changed (their tap or switched off after no activity — WS staff.availability)
        `#${inbox.available ? 1 : 0}:` +
        inbox.team.map((t) => `${t.id}:${t.available ? 1 : 0}`).join(','),
      () => schedule(),
    )
  }

  function stop(): void {
    users = Math.max(0, users - 1)
    if (users > 0) return
    if (timer) clearInterval(timer)
    if (soon) clearTimeout(soon)
    stopWatch?.()
    timer = null
    soon = null
    stopWatch = null
  }

  return { load, error, refresh, schedule, start, stop }
})
